import React from 'react';
import { observer } from 'mobx-react';
import { GridValidRowModel } from '@mui/x-data-grid';

import { VatixError } from 'components/Error/types';
import Grid from 'components/Grid/Grid';
import { ColumnDefinition } from 'components/Grid/types';
import Chip from 'components/Chip';

import { AuditsIcon } from 'containers/Dashboard/components/SideBar/Icons/AuditsIcon';
import { ProtectorIcon } from 'containers/Dashboard/components/SideBar/Icons/ProtectorIcon';
import { IncidentIcon } from 'containers/Dashboard/components/SideBar/Icons/IncidentsIcon';

import { ProductLicenseName } from 'core/constants';

import { env } from 'config';

import { DocumentIcon } from 'containers/Dashboard/components/SideBar/Icons/DocumentIcon';
import { RiskIcon } from 'containers/Dashboard/components/SideBar/Icons/RiskIcon';

import { colors } from 'theme/colors';

import LicensesAssignedCell from '../LicensesAssignedCell';

const LicenseIconMapping: Record<string, React.ReactElement> = Object.freeze({
  workflows: <AuditsIcon fill={colors.primary.main} />,
  protectorAlarms: <ProtectorIcon fill={colors.primary.main} />,
  events: <IncidentIcon fill={colors.primary.main} />,
  documents: <DocumentIcon fill={colors.primary.main} />,
  risks: <RiskIcon fill={colors.primary.main} />,
});

const basicColumns = [
  {
    field: 'name',
    headerName: 'Name',
    renderCell: ({ value }: { value: string }) => (
      <Chip
        size="small"
        variant="filled"
        colors={{ background: 'transparent', iconLeftBackground: 'rgba(7, 114, 229, 0.05)' }}
        label={ProductLicenseName[value]}
        iconLeft={LicenseIconMapping[value]}
        iconLeftDisableInnerMargin
        iconLeftGapWidth={12}
      />
    ),
  },
  {
    field: 'maxSeats',
    headerName: 'Number of licences',
  },
  {
    field: 'overview',
    headerName: 'Licences assigned',
    renderCell: ({ value }: { value: { taken: number; max: number } }) => (
      <LicensesAssignedCell licensesAssigned={value} />
    ),
  },
] as unknown as ColumnDefinition[];

const LicensesList: React.FunctionComponent<{ onError: (error: VatixError) => void }> = ({ onError }) => {
  const transformData = (
    payload: unknown
  ): {
    count: number;
    results: GridValidRowModel[];
  } => {
    const response = payload as { [key: string]: { seatsTaken: number; maxSeats: number } };
    return {
      count: Object.keys(response).length,
      results: Object.keys(response)
        .filter((name) => name !== 'protectorNotify' && name !== 'protectorIncidents')
        .map((name) => ({
          ...response[name],
          name,
          uuid: name,
          overview: { taken: response[name].seatsTaken, max: response[name].maxSeats },
        })),
    };
  };

  return (
    <Grid
      basicColumns={basicColumns}
      dataURL={`${env.REACT_APP_ADMIN_API_URL}licenses/`}
      onError={onError}
      sortBy={{ field: 'name', sort: 'asc' }}
      transformData={transformData}
      gridPagination={false}
      disableColumnSelector
      showHeader={false}
      showQuickFilter={false}
    />
  );
};

export default observer(LicensesList);
